import { normalizeImportPath } from './asset-inventory';

const EXTERNAL_REFERENCE_PREFIXES = [
  'http://',
  'https://',
  '//',
  'data:',
  'mailto:',
  'tel:',
  'javascript:',
];

export type ResolvedImportReference =
  | {
      kind: 'local';
      originalReference: string;
      normalizedReference: string;
      resolvedPath: string;
    }
  | {
      kind: 'external';
      originalReference: string;
    }
  | {
      kind: 'fragment';
      originalReference: string;
    }
  | {
      kind: 'empty';
      originalReference: string;
    }
  | {
      kind: 'unsafe';
      originalReference: string;
      resolvedPath: string;
    };

function stripQueryAndFragment(reference: string): string {
  return reference.split(/[?#]/, 1)[0] ?? '';
}

function isExternalReference(reference: string): boolean {
  const lowerReference = reference.toLowerCase();

  return EXTERNAL_REFERENCE_PREFIXES.some((prefix) =>
    lowerReference.startsWith(prefix),
  );
}

function isFragmentReference(reference: string): boolean {
  return reference.startsWith('#');
}

function resolveRootRelativePath(reference: string): string {
  return normalizeImportPath(reference);
}

function normalizeBrowserPath(
  value: string,
): string {
  const parts = value.replace(/\\/g, '/').split('/');
  const resolvedParts: string[] = [];
  let escapedRootCount = 0;

  for (const part of parts) {
    if (!part || part === '.') continue;

    if (part === '..') {
      if (resolvedParts.length > 0) {
        resolvedParts.pop();
      } else {
        escapedRootCount += 1;
      }

      continue;
    }

    resolvedParts.push(part);
  }

  const escapedPrefix = '../'.repeat(escapedRootCount);

  return `${escapedPrefix}${resolvedParts.join('/')}`;
}

function resolvePageRelativePath(
  pageSourcePath: string,
  reference: string,
): string {
  const normalizedPagePath =
    normalizeImportPath(pageSourcePath);

  const finalSlash =
    normalizedPagePath.lastIndexOf('/');

  const pageDirectory =
    finalSlash >= 0
      ? normalizedPagePath.slice(0, finalSlash)
      : '';

  const combinedPath =
    pageDirectory
      ? `${pageDirectory}/${reference}`
      : reference;

  return normalizeImportPath(
    normalizeBrowserPath(combinedPath),
  );
}

export function resolveImportReference(
  pageSourcePath: string,
  reference: string,
): ResolvedImportReference {
  const trimmedReference = reference.trim();

  if (!trimmedReference) {
    return {
      kind: 'empty',
      originalReference: reference,
    };
  }

  if (isFragmentReference(trimmedReference)) {
    return {
      kind: 'fragment',
      originalReference: reference,
    };
  }

  if (isExternalReference(trimmedReference)) {
    return {
      kind: 'external',
      originalReference: reference,
    };
  }

  const withoutQueryOrFragment =
    stripQueryAndFragment(trimmedReference);

  if (!withoutQueryOrFragment) {
    return {
      kind: 'empty',
      originalReference: reference,
    };
  }

  const decodedReference = decodeURIComponent(
    withoutQueryOrFragment,
  );

  const resolvedPath = decodedReference.startsWith('/')
    ? resolveRootRelativePath(decodedReference)
    : resolvePageRelativePath(
        pageSourcePath,
        decodedReference,
      );

  if (
    resolvedPath === '..' ||
    resolvedPath.startsWith('../')
  ) {
    return {
      kind: 'unsafe',
      originalReference: reference,
      resolvedPath,
    };
  }

  return {
    kind: 'local',
    originalReference: reference,
    normalizedReference: decodedReference,
    resolvedPath,
  };
}

export function resolveLocalPath(
  pageSourcePath: string,
  reference: string,
): string | null {
  const result = resolveImportReference(
    pageSourcePath,
    reference,
  );

  return result.kind === 'local'
    ? result.resolvedPath
    : null;
}
